import {
  createBillingClient,
  createCatalogClient,
  createCustomersClient,
  createIdentityClient,
  createOrderingClient,
  createTallySyncClient,
  createWarehouseClient,
} from "@steelyard/api-client";
import type { ProblemDetails } from "../types";

/**
 * Every backend-shaped call this app makes, through the generated `@steelyard/api-client` clients
 * (FR-006) — never a hand-written `fetch` to a raw path string. Ported role of the Next.js
 * `apps/admin/lib/api.ts`, minus the render/writable client split and the session middleware:
 * those existed only because a Next.js Server Component could not write a cookie mid-render and
 * had to attach its own bearer token. Here every call is a normal browser fetch to the BFF's own
 * same origin, with `credentials: "include"` — the BFF is what attaches the bearer token and
 * renews on 401 (016 research.md R3/R4). There is no gateway URL anywhere in this file, and no
 * `VITE_`-prefixed environment variable — the SPA has no backend origin to leak.
 */

/** FR-028's hard bound on a hung request — matches the Next.js version's REQUEST_TIMEOUT_MS. */
const REQUEST_TIMEOUT_MS = 15_000;

/** Every client gets this: a hard request timeout, and same-origin credentials for the BFF's httpOnly cookies. */
const withCredentialsAndTimeout = {
  onRequest({ request }: { request: Request }) {
    return new Request(request, {
      credentials: "include",
      signal: request.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  },
};

/** Same-origin — every request goes to the BFF, whether the dev-server proxy or the BFF's own production origin (016 research.md R3). */
const BFF_BASE_URL = "";

export function catalog() {
  const client = createCatalogClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

export function identity() {
  const client = createIdentityClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

export function customers() {
  const client = createCustomersClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

/** Routed at `/api/orders`, not `/api/ordering` — see packages/api-client's own note on this. */
export function ordering() {
  const client = createOrderingClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

/** One client for invoicing, payments, registration and dispatch — one service, one gateway prefix. */
export function billing() {
  const client = createBillingClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

export function tallySync() {
  const client = createTallySyncClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

/** Serves both `005` (packing) and `007` (inventory) — one Warehouse service, one client. */
export function warehouse() {
  const client = createWarehouseClient(BFF_BASE_URL);
  client.use(withCredentialsAndTimeout);
  return client;
}

/**
 * Thrown on any non-2xx response. Carries the gateway's own RFC 9457 problem document untouched
 * (constitution VI: show it, don't paraphrase it) — the BFF relays a non-2xx response's body and
 * status unmodified (contracts/bff-contract.md).
 */
export class ApiError extends Error {
  status: number;
  title?: string;
  detail?: string;
  type?: string;
  /** The full problem document, for callers that need a service-specific extension field (e.g. a `code`). */
  raw: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null) {
    super(problem?.title ?? `Request failed with status ${status}.`);
    this.status = status;
    this.title = problem?.title;
    this.detail = problem?.detail;
    this.type = problem?.type;
    this.raw = problem;
  }
}

/**
 * Every generated operation types its response as `undefined` unless the service actually
 * declares one (GAP-4/GAP-5 — identity declares none at all, catalog/billing declare most).
 * `unwrap` is the one seam that turns openapi-fetch's `{data, error, response}` into either the
 * (runtime-unparsed) body or a thrown `ApiError` — callers that need a typed shape parse `data`
 * themselves at runtime (`parseAccount`, `parsePermissions`, ...), the same discipline the
 * Next.js version already used.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok) {
    const problem = (error ?? null) as ProblemDetails | null;
    throw new ApiError(response.status, problem);
  }
  return data as T;
}
